import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { TIPOS, fechaHora, seguridadApi, severidad } from './seguridadApi';

/** S-6 Bitacora de seguridad (ADR-002 S7): solo lectura, con filtros y verificacion de la cadena. */
const hoy = () => new Date().toISOString().substring(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().substring(0, 10);

const RESULTADOS = [
    { label: 'Todos', value: '' },
    { label: 'Correcto', value: 'OK' },
    { label: 'Fallo', value: 'FALLO' },
    { label: 'Denegado', value: 'DENEGADO' },
    { label: 'Informativo', value: 'INFO' }
];

const SeguridadBitacora = () => {
    const toast = useRef(null);
    const [filtros, setFiltros] = useState({ desde: haceDias(7), hasta: hoy(), usuario: '', tipo: '', resultado: '' });
    const [pagina, setPagina] = useState(0);
    const [datos, setDatos] = useState({ total: 0, filas: [], tipos: [] });
    const [cargando, setCargando] = useState(false);
    const [integridad, setIntegridad] = useState(null);

    const buscar = useCallback(async (p = 0) => {
        setCargando(true);
        try {
            setDatos(await seguridadApi.bitacora({ ...filtros, pagina: p }));
            setPagina(p);
        } catch (e) {
            toast.current && toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
        } finally {
            setCargando(false);
        }
    }, [filtros]);

    useEffect(() => { buscar(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const verificar = async () => {
        try {
            setIntegridad(await seguridadApi.verificarBitacora());
        } catch (e) {
            toast.current && toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
        }
    };

    const campo = (k) => (e) => setFiltros({ ...filtros, [k]: e.target ? e.target.value : e.value });

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <h4 className="m-0">Bitacora de seguridad</h4>
                <Button label="Verificar integridad" icon="pi pi-verified" className="p-button-outlined" onClick={verificar} />
            </div>
            {integridad && (
                <Message className="mb-3 w-full justify-content-start" severity={integridad.integra ? 'success' : 'error'}
                    text={integridad.integra ? 'La bitacora esta integra: ninguna fila fue alterada ni borrada.'
                        : `La cadena se rompe en la fila ${integridad.primeraFilaAlterada}: hubo una alteracion por fuera de OPEM.`} />
            )}
            <div className="grid formgrid p-fluid mb-2">
                <div className="field col-6 md:col-2">
                    <label htmlFor="bit-desde">Desde</label>
                    <InputText id="bit-desde" type="date" value={filtros.desde} onChange={campo('desde')} />
                </div>
                <div className="field col-6 md:col-2">
                    <label htmlFor="bit-hasta">Hasta</label>
                    <InputText id="bit-hasta" type="date" value={filtros.hasta} onChange={campo('hasta')} />
                </div>
                <div className="field col-12 md:col-2">
                    <label htmlFor="bit-usuario">Usuario</label>
                    <InputText id="bit-usuario" value={filtros.usuario} onChange={campo('usuario')} placeholder="Usuario o nombre"
                        onKeyDown={(e) => { if (e.key === 'Enter') buscar(0); }} />
                </div>
                <div className="field col-6 md:col-2">
                    <label htmlFor="bit-tipo">Evento</label>
                    <Dropdown inputId="bit-tipo" value={filtros.tipo} onChange={campo('tipo')} showClear
                        options={(datos.tipos || []).map((t) => ({ label: TIPOS[t] || t, value: t }))} placeholder="Todos" />
                </div>
                <div className="field col-6 md:col-2">
                    <label htmlFor="bit-resultado">Resultado</label>
                    <Dropdown inputId="bit-resultado" value={filtros.resultado} onChange={campo('resultado')} options={RESULTADOS} />
                </div>
                <div className="field col-12 md:col-2 flex align-items-end">
                    <Button label="Buscar" icon="pi pi-search" onClick={() => buscar(0)} loading={cargando} />
                </div>
            </div>
            <DataTable value={datos.filas} size="small" lazy paginator rows={100} first={pagina * 100} totalRecords={datos.total}
                onPage={(e) => buscar(e.page)} loading={cargando} emptyMessage="Sin registros con esos filtros" responsiveLayout="scroll">
                <Column header="Fecha" body={(r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fechaHora(r.fecha)}</span>} />
                <Column header="Usuario" body={(r) => <div>{r.username || '—'}<br /><small className="text-600">{r.nombre}</small></div>} />
                <Column header="Evento" body={(r) => <Tag severity={severidad(r.resultado)} value={TIPOS[r.tipo] || r.tipo} />} />
                <Column header="IP" field="ip" />
                <Column header="Detalle" field="detalle" style={{ maxWidth: '28rem' }} />
                <Column header="Hecho por" field="actor" />
            </DataTable>
        </div>
    );
};

export default SeguridadBitacora;
